import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";

declare let Stripe: (key: string, options?: Record<string, unknown>) => StripeInstance;

// Stripe Type Definitions
interface StripeElement {
  mount(selector: string): void;
  unmount(): void;
  on(event: string, handler: (event: StripeElementChangeEvent) => void): void;
}

interface StripeElements {
  create(type: string, options?: Record<string, unknown>): StripeElement;
}

interface StripeInstance {
  elements(): StripeElements;
  createPaymentMethod(options: Record<string, unknown>): Promise<{ paymentMethod?: { id: string }; error?: { message: string } }>;
  paymentRequest(options: Record<string, unknown>): PaymentRequest;
}

interface StripeElementChangeEvent {
  error?: { message: string };
  complete: boolean;
  brand?: string;
  empty?: boolean;
}

interface PaymentRequestPaymentEvent {
  paymentMethod: { id: string };
  payerName: string;
  payerEmail: string;
  complete(status: string): void;
}

interface PaymentRequest {
  canMakePayment(): Promise<{ applePay?: boolean; googlePay?: boolean }>;
  on(event: string, handler: (event: PaymentRequestPaymentEvent) => void): void;
  update(options: { total: { label: string; amount: number } }): void;
}

interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

// Interfaces
export interface StripePaymentFormConfig {
  showName?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showAddress?: boolean;
  showZipCode?: boolean;
  showSaveCard?: boolean;
  showApplePay?: boolean;
  showGooglePay?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  formTitle?: string;
  formSubtitle?: string;
  stripePublicKey: string;
  locale?: string;
  applePayOptions?: {
    buttonType?: "plain" | "buy" | "set-up" | "donate" | "check-out" | "book" | "subscribe";
    buttonTheme?: "dark" | "light" | "light-outline";
    buttonHeight?: string;
  };
  save_button?: string;
}

export interface StripePaymentFormData {
  id?: string;
  paymentMethodId?: string;
  billingDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  saveCard?: boolean;
}

export interface StripeElementStyle {
  base: {
    color: string;
    fontSize: string;
    fontFamily: string;
    "::placeholder": {
      color: string;
    };
  };
  invalid: {
    color: string;
  };
}

@Component({
    selector: "app-stripe-payment-form",
    templateUrl: "./stripe-payment-form.component.html",
    styleUrls: ["./stripe-payment-form.component.scss"],
    standalone: false
})
export class StripePaymentFormComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild("cardNumber") cardNumberRef: ElementRef;
  @ViewChild("cardExpiry") cardExpiryRef: ElementRef;
  @ViewChild("cardCvc") cardCvcRef: ElementRef;
  @ViewChild("postalCode") postalCodeRef: ElementRef;

  @Input() config: StripePaymentFormConfig = {
    showName: true,
    showZipCode: true,
    submitButtonText: "Submit Payment",
    cancelButtonText: "Cancel",
    stripePublicKey: "",
    applePayOptions: {
      buttonType: "plain",
      buttonTheme: "dark",
      buttonHeight: "48px",
    },
  };
  @Input() stripeFormType = "";
  @Input() customContent: TemplateRef<unknown>;
  @Input() termsAccepted = true;
  @Input() kioskChecked = true;
  @Input() disclosureAgreementChecked = true;
  @Input() receiveDocumentsByEmailChecked = true;
  @Input() isAssurant = false;
  @Input() totalPayable = 0;
  @Input() loading = false;
  @Input() externalLoadingControl = false;

  @Input() checkboxError: string | null = null;

  @Output() formSubmit = new EventEmitter<StripePaymentFormData>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() formError = new EventEmitter<string>();
  @Output() applePayComplete = new EventEmitter<StripePaymentFormData>();

  paymentForm: FormGroup;
  showApplePayButton = false;
  showGooglePayButton = false;
  _termsAccepted = false;

  // Stripe elements
  private stripe: StripeInstance;
  private elements: StripeElements;
  private cardNumber: StripeElement;
  private cardExpiry: StripeElement;
  private cardCvc: StripeElement;
  private postalCode: StripeElement;
  private paymentRequest: PaymentRequest;
  private paymentRequestButton: StripeElement;

  errors = {
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
    postalCode: "",
  };


  cardBrand = "";

  stripeElementsValid = {
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
    postalCode: false,
  };

  private destroy$ = new Subject<void>();

  // Default Stripe element styles
  private readonly STRIPE_ELEMENT_STYLE: StripeElementStyle = {
    base: {
      color: "#14171C",
      fontSize: "16px",
      fontFamily: 'Inter, system-ui, sans-serif',
      "::placeholder": {
        color: "#667080",
      },
    },
    invalid: {
      color: "#e74c3c",
    },
  };

  constructor(private fb: FormBuilder, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.initializeForm();
    this._termsAccepted = this.termsAccepted;
  }

  ngAfterViewInit(): void {
    if (this.config.stripePublicKey) {
      this.initializeStripe();
    } else {
      this.formError.emit("Payment system configuration error");
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["termsAccepted"]) {
      this._termsAccepted = changes["termsAccepted"].currentValue;
    }

    if (changes["totalPayable"] && !changes["totalPayable"].firstChange) {
      this.updatePaymentRequest();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unmountStripeElements();
  }

  private initializeForm(): void {
    const formControls: Record<string, [string | boolean | null, ValidatorFn[]] | [string | boolean | null]> = {};

    // Dynamically add form controls based on config
    if (this.config.showName) {
      formControls.name = ["", [Validators.required]];
    }

    if (this.config.showEmail) {
      formControls.email = ["", [Validators.required, Validators.email]];
    }

    if (this.config.showPhone) {
      formControls.phone = ["", [Validators.required]];
    }

    if (this.config.showAddress) {
      formControls.addressLine1 = ["", [Validators.required]];
      formControls.addressLine2 = [""];
      formControls.city = ["", [Validators.required]];
      formControls.state = ["", [Validators.required]];
      formControls.country = ["", [Validators.required]];
    }

    if (this.config.showSaveCard) {
      formControls.saveCard = [false, []];
    }

    this.paymentForm = this.fb.group(formControls);
  }

  private initializeStripe(): void {
    this.stripe = Stripe(this.config.stripePublicKey, {
      locale: this.config.locale || "en",
    });

    this.elements = this.stripe.elements();
    this.createStripeElements();
    this.mountStripeElements();

    if (this.config.showApplePay || this.config.showGooglePay) {
      this.setupPaymentRequest();
    }
  }

  private createStripeElements(): void {
    this.cardNumber = this.elements.create("cardNumber", {
      style: this.STRIPE_ELEMENT_STYLE,
      showIcon: true,
      iconStyle: "solid",
    });

    this.cardExpiry = this.elements.create("cardExpiry", {
      style: this.STRIPE_ELEMENT_STYLE,
    });

    this.cardCvc = this.elements.create("cardCvc", {
      style: this.STRIPE_ELEMENT_STYLE,
    });

    this.postalCode = this.elements.create("postalCode", {
      style: this.STRIPE_ELEMENT_STYLE,
    });
  }

  private mountStripeElements(): void {
    setTimeout(() => {
      if (this.cardNumberRef?.nativeElement) {
        this.cardNumber.mount(this.cardNumberRef.nativeElement);
        this.cardNumber.on("change", (event) => this.handleElementChange("cardNumber", event));
      }

      if (this.cardExpiryRef?.nativeElement) {
        this.cardExpiry.mount(this.cardExpiryRef.nativeElement);
        this.cardExpiry.on("change", (event) => this.handleElementChange("cardExpiry", event));
      }

      if (this.cardCvcRef?.nativeElement) {
        this.cardCvc.mount(this.cardCvcRef.nativeElement);
        this.cardCvc.on("change", (event) => this.handleElementChange("cardCvc", event));
      }

      if (this.postalCodeRef?.nativeElement) {
        this.postalCode.mount(this.postalCodeRef.nativeElement);
        this.postalCode.on("change", (event) => this.handleElementChange("postalCode", event));
      }
    }, 100);
  }

  private unmountStripeElements(): void {
    if (this.cardNumber) this.cardNumber.unmount();
    if (this.cardExpiry) this.cardExpiry.unmount();
    if (this.cardCvc) this.cardCvc.unmount();
    if (this.postalCode) this.postalCode.unmount();
  }

  private handleElementChange(element: string, event: StripeElementChangeEvent): void {
    this.errors[element] = event.error?.message || "";
    this.stripeElementsValid[element] = event.complete && !event.error;

    if (element === "cardNumber" && event.brand) {
      this.cardBrand = event.brand;
    }

    if (element === "postalCode") {
      if (event.empty) {
        this.errors.postalCode = "ZIP code is required";
        this.stripeElementsValid.postalCode = false;
      } else if (!event.complete) {
        this.errors.postalCode = "ZIP code must be 5 digits";
        this.stripeElementsValid.postalCode = false;
      } else {
        this.errors.postalCode = "";
        this.stripeElementsValid.postalCode = true;
      }
    }
  }

  private async setupPaymentRequest(): Promise<void> {
    this.paymentRequest = this.stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Payment",
        amount: this.totalPayable,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const result = await this.paymentRequest.canMakePayment();
    if (result) {
      if (result.applePay && this.config.showApplePay) {
        this.showApplePayButton = true;
      }
      if (result.googlePay && this.config.showGooglePay) {
        this.showGooglePayButton = true;
      }

      if (this.showApplePayButton || this.showGooglePayButton) {
        this.createPaymentRequestButton();
      }
    }

    this.paymentRequest.on("paymentmethod", this.handlePaymentRequestPayment.bind(this));
  }

  private createPaymentRequestButton(): void {
    this.paymentRequestButton = this.elements.create("paymentRequestButton", {
      paymentRequest: this.paymentRequest,
      style: {
        paymentRequestButton: {
          theme: "dark",
          height: "48px",
          type: "default",
        },
      },
    });

    setTimeout(() => {

      const element = document.getElementById("apple-pay-button");
      if (element) {
        this.paymentRequestButton.mount("#apple-pay-button");
      }
    }, 100);
  }

  async handleSubmit(): Promise<void> {
    if (!this.paymentForm.valid) {
      this.markFormGroupTouched(this.paymentForm);
      return;
    }

    this.loading = true;

    const formValues = this.paymentForm.value;

    try {
      const billingDetails: BillingDetails = {};

      if (this.config.showName && formValues.name) {
        billingDetails.name = formValues.name;
      }

      if (this.config.showEmail && formValues.email) {
        billingDetails.email = formValues.email;
      }

      if (this.config.showPhone && formValues.phone) {
        billingDetails.phone = formValues.phone;
      }

      if (this.config.showAddress || this.config.showZipCode) {
        billingDetails.address = {};

        if (this.config.showAddress) {
          billingDetails.address.line1 = formValues.addressLine1;
          billingDetails.address.line2 = formValues.addressLine2;
          billingDetails.address.city = formValues.city;
          billingDetails.address.state = formValues.state;
          billingDetails.address.country = formValues.country;
        }

        if (this.config.showZipCode && formValues.zipCode) {
          billingDetails.address.postal_code = formValues.zipCode;
        }
      }

      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: "card",
        card: this.cardNumber,
        billing_details: billingDetails,
      });

      if (error) {
        this.formError.emit(error.message);
      } else {
        const paymentData: StripePaymentFormData = {
          paymentMethodId: paymentMethod.id,
          billingDetails: billingDetails,
        };

        if (this.config.showSaveCard) {
          paymentData.saveCard = formValues.saveCard;
        }

        if (this.stripeFormType == "resident-payment")
          this.formSubmit.emit(paymentMethod);
        else this.formSubmit.emit(paymentData);
      }
    } catch (error) {
      this.formError.emit("An unexpected error occurred");
    } finally {
      if (!this.externalLoadingControl) {
        this.loading = false;
      }
    }
  }

  onApplePayButtonClick(): void {
    if (!this.termsAccepted || !this.kioskChecked || !this.disclosureAgreementChecked) {
      this.formError.emit("An unexpected error occurred");
      return;
    }
  }

  private async handlePaymentRequestPayment(event: PaymentRequestPaymentEvent): Promise<void> {
    try {
      const paymentData: StripePaymentFormData = {
        id: event.paymentMethod.id,
        billingDetails: {
          name: event.payerName,
          email: event.payerEmail,
        },
      };

      this.applePayComplete.emit(paymentData);
      event.complete("success");
    } catch (error) {
      event.complete("fail");
      this.formError.emit("Payment failed");
    }
  }

  handleCancel(): void {
    this.formCancel.emit();
  }

  resetValidation(): void {
    this.stripeElementsValid = {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
      postalCode: false,
    };
    this.errors = {
      cardNumber: "",
      cardExpiry: "",
      cardCvc: "",
      postalCode: "",
    };
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  hasError(field: string): boolean {
    const control = this.paymentForm.get(field);
    return !!(control?.invalid && control?.touched);
  }

  getError(field: string, error: string): boolean {
    const control = this.paymentForm.get(field);
    return !!(control?.hasError(error) && control?.touched);
  }

  get isFormValid(): boolean {
    const formValid = this.paymentForm.valid;
    const stripeElementsValid =
      this.stripeElementsValid.cardNumber && this.stripeElementsValid.cardExpiry && this.stripeElementsValid.cardCvc && this.stripeElementsValid.postalCode;
    const termsValid = this._termsAccepted;
    return formValid && stripeElementsValid && termsValid && this.kioskChecked && this.disclosureAgreementChecked;
  }

  getValidationDetails(): {
    formValid: boolean;
    stripeElements: { cardNumber: boolean; cardExpiry: boolean; cardCvc: boolean };
    termsAccepted: boolean;
    isFormValid: boolean;
    formErrors: Record<string, unknown> | null;
    stripeErrors: { cardNumber: string; cardExpiry: string; cardCvc: string };
  } {
    return {
      formValid: this.paymentForm.valid,
      stripeElements: this.stripeElementsValid,
      termsAccepted: this._termsAccepted,
      isFormValid: this.isFormValid,
      formErrors: this.paymentForm.errors,
      stripeErrors: this.errors,
    };
  }

  isCardNumberValid(): boolean {
    return this.stripeElementsValid.cardNumber;
  }

  isCardExpiryValid(): boolean {
    return this.stripeElementsValid.cardExpiry;
  }

  isCardCvcValid(): boolean {
    return this.stripeElementsValid.cardCvc;
  }

  isPostalCodeValid(): boolean {
    return this.stripeElementsValid.postalCode;
  }

  isZipCodeValid(): boolean {
    const zipCodeControl = this.paymentForm.get("zipCode");
    return zipCodeControl ? zipCodeControl.valid && zipCodeControl.touched : true;
  }

  private updatePaymentRequest(): void {
    if (this.paymentRequest && this.stripe) {
      this.paymentRequest.update({
        total: {
          label: "Payment",
          amount: this.totalPayable,
        },
      });
    }
  }
}
